/**
 * 密码哈希工具
 *
 * 优先使用 Web Crypto API，非安全上下文（HTTP）自动降级为纯 JS 实现并输出警告
 */

/** 字节数组转 Hex 字符串 */
function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** 纯 JS SHA-256 实现（非安全上下文降级用） */
function sha256JS(data: Uint8Array): Uint8Array {
  const K = [
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1,
    0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
    0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786,
    0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147,
    0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
    0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b,
    0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a,
    0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
    0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
  ];

  const rotr = (n: number, x: number) => (x >>> n) | (x << (32 - n));
  const ch = (x: number, y: number, z: number) => (x & y) ^ (~x & z);
  const maj = (x: number, y: number, z: number) => (x & y) ^ (x & z) ^ (y & z);
  const sigma0 = (x: number) => rotr(2, x) ^ rotr(13, x) ^ rotr(22, x);
  const sigma1 = (x: number) => rotr(6, x) ^ rotr(11, x) ^ rotr(25, x);
  const gamma0 = (x: number) => rotr(7, x) ^ rotr(18, x) ^ (x >>> 3);
  const gamma1 = (x: number) => rotr(17, x) ^ rotr(19, x) ^ (x >>> 10);

  const bitLen = data.length * 8;
  const padded = new Uint8Array(Math.ceil((data.length + 9) / 64) * 64);
  padded.set(data);
  padded[data.length] = 0x80;
  const view = new DataView(padded.buffer);
  view.setUint32(padded.length - 4, bitLen, false);

  let [h0, h1, h2, h3, h4, h5, h6, h7] = [
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c,
    0x1f83d9ab, 0x5be0cd19,
  ];

  for (let offset = 0; offset < padded.length; offset += 64) {
    const W = new Array<number>(64);
    for (let t = 0; t < 16; t++) W[t] = view.getUint32(offset + t * 4, false);
    for (let t = 16; t < 64; t++)
      W[t] = (gamma1(W[t - 2]) + W[t - 7] + gamma0(W[t - 15]) + W[t - 16]) | 0;

    let [a, b, c, d, e, f, g, h] = [h0, h1, h2, h3, h4, h5, h6, h7];
    for (let t = 0; t < 64; t++) {
      const t1 = (h + sigma1(e) + ch(e, f, g) + K[t] + W[t]) | 0;
      const t2 = (sigma0(a) + maj(a, b, c)) | 0;
      h = g;
      g = f;
      f = e;
      e = (d + t1) | 0;
      d = c;
      c = b;
      b = a;
      a = (t1 + t2) | 0;
    }
    h0 = (h0 + a) | 0;
    h1 = (h1 + b) | 0;
    h2 = (h2 + c) | 0;
    h3 = (h3 + d) | 0;
    h4 = (h4 + e) | 0;
    h5 = (h5 + f) | 0;
    h6 = (h6 + g) | 0;
    h7 = (h7 + h) | 0;
  }

  const result = new Uint8Array(32);
  const rv = new DataView(result.buffer);
  [h0, h1, h2, h3, h4, h5, h6, h7].forEach((v, i) => {
    rv.setUint32(i * 4, v, false);
  });
  return result;
}

/** 支持的哈希算法 */
export type HashAlgorithm = 'sha256';

/** 是否已输出过降级警告 */
let fallbackWarned = false;

/**
 * 对密码进行哈希处理（SHA-256）
 *
 * 优先使用 Web Crypto API，非安全上下文自动降级为纯 JS 实现并输出控制台警告
 *
 * @param password - 明文密码
 * @param algorithm - 哈希算法，默认 'sha256'
 * @returns 哈希后的字符串 (hex)
 */
export async function hashPassword(
  password: string,
  algorithm: HashAlgorithm = 'sha256',
): Promise<string> {
  if (!password) {
    throw new Error('[Crypto] Password cannot be empty');
  }

  if (algorithm !== 'sha256') {
    throw new Error(
      `[Crypto] Unsupported hash algorithm: ${algorithm}. Only 'sha256' is supported.`,
    );
  }

  const encoded = new TextEncoder().encode(password);

  // 优先使用 Web Crypto API
  const subtle = globalThis.crypto?.subtle;
  if (subtle) {
    const hashBuffer = await subtle.digest('SHA-256', encoded);
    return bytesToHex(new Uint8Array(hashBuffer));
  }

  // 非安全上下文降级：输出警告 + 纯 JS 实现
  if (!fallbackWarned) {
    fallbackWarned = true;
    console.error(
      '[Crypto] Web Crypto API 不可用（非安全上下文），已降级为纯 JS SHA-256 实现，建议使用 HTTPS 访问',
    );
  }
  return bytesToHex(sha256JS(encoded));
}

/**
 * 生成随机 hex 字符串
 *
 * @param length - 长度，默认 32
 * @returns 随机 hex 字符串
 */
export function generateRandomHex(length: number = 32): string {
  const bytes = new Uint8Array(Math.ceil(length / 2));
  crypto.getRandomValues(bytes);
  return bytesToHex(bytes).slice(0, length);
}
